import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { GetNotificationPreferencesResponse } from 'sdk';
import { appConfig } from 'shared';
import { HelpText } from '~/modules/common/help-text';
import { ToolCard } from '~/modules/common/tool-card';
import { notificationPreferencesQueryOptions, useUpdateNotificationPreferencesMutation } from '~/modules/notification/query';
import { usePushSubscription } from '~/modules/notification/use-push-subscription';
import { FieldContent, FieldLabel, FieldLayout, FieldTitle } from '~/modules/ui/field';
import { Label } from '~/modules/ui/label';
import { RadioGroup, RadioGroupItem } from '~/modules/ui/radio-group';
import { Switch } from '~/modules/ui/switch';

/** Cadences are mutually exclusive: one enum column, and each notification is digested once. */
const digestOptions = [
  { value: 'off', label: 'c:notifications.digest_off' },
  { value: 'daily', label: 'c:notifications.digest_daily' },
  { value: 'weekly', label: 'c:notifications.digest_weekly' },
] as const satisfies { value: GetNotificationPreferencesResponse['digest']; label: string }[];

/** Each switch adds a delivery: the inbox always fills, so with every switch off a mention still arrives. */
export function AccountNotificationsCard() {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(notificationPreferencesQueryOptions());
  const { mutate: updatePreferences } = useUpdateNotificationPreferencesMutation();
  const push = usePushSubscription();

  return (
    <ToolCard label="c:notifications" description={t('c:notifications.text')}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Switch id="mentionEmail" checked={data.mentionEmail} onCheckedChange={(mentionEmail) => updatePreferences({ mentionEmail })} />
          <Label htmlFor="mentionEmail">{t('c:notifications.mention_email')}</Label>
        </div>

        {appConfig.has.commentEmail && (
          <div className="flex items-center gap-4">
            <Switch id="commentEmail" checked={data.commentEmail} onCheckedChange={(commentEmail) => updatePreferences({ commentEmail })} />
            <Label htmlFor="commentEmail">{t('c:notifications.comment_email')}</Label>
          </div>
        )}

        {push.supported && (
          <div className="flex items-center gap-4">
            <Switch
              id="pushEnabled"
              checked={push.enabled}
              disabled={push.busy}
              onCheckedChange={(checked) => (checked ? push.enable() : push.disable())}
            />
            <Label htmlFor="pushEnabled">{t('c:notifications.push')}</Label>
          </div>
        )}

        <div className="mt-3">
          <HelpText content={t('c:notifications.digest.text')}>
            <Label id="digest-label">{t('c:notifications.digest')}</Label>
          </HelpText>
          {/* RadioGroup's `grid` already stacks the tiles, so only the desktop row needs a class */}
          <RadioGroup
            aria-labelledby="digest-label"
            value={data.digest}
            onValueChange={(digest) => updatePreferences({ digest })}
            className="lg:grid-cols-3"
          >
            {digestOptions.map(({ value, label }) => (
              <FieldLabel key={value} htmlFor={`digest-${value}`} className="cursor-pointer">
                <FieldLayout orientation="horizontal">
                  <RadioGroupItem id={`digest-${value}`} value={value} />
                  <FieldContent>
                    <FieldTitle>{t(label)}</FieldTitle>
                  </FieldContent>
                </FieldLayout>
              </FieldLabel>
            ))}
          </RadioGroup>
        </div>
      </div>
    </ToolCard>
  );
}
